// ── MessageList ────────────────────────────────────────────────────
//
// Renders the chronological scroll of messages + interleaved internal
// notes + day separators. Auto-scrolls to bottom when a NEW message
// arrives at the tail, unless the user has scrolled up to read history.
// Infinite scroll UP via cursor pagination is reserved for Phase 8b
// — for the MVP the conversation detail already returns last 50 and
// realtime keeps them in sync.

import { useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { MessageBubble } from './MessageBubble';
import { dayKey, formatDaySeparator, formatMessageTime } from '../utils/time-format';
import type { InternalNote, WhatsappMessage } from '../api/whatsapp.api';

interface MessageListProps {
  messages: WhatsappMessage[];
  notes: InternalNote[];
}

/**
 * Merge messages + notes into a single chronological timeline, then
 * group by day for separator headers.
 */
type TimelineItem =
  | { kind: 'message'; iso: string; data: WhatsappMessage }
  | { kind: 'note'; iso: string; data: InternalNote };

export function MessageList({ messages, notes }: MessageListProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const lastCountRef = useRef(messages.length);

  // Combine + sort ascending (oldest first → chat reads top-to-bottom).
  const timeline = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [
      ...messages.map<TimelineItem>((m) => ({ kind: 'message', iso: m.timestamp, data: m })),
      ...notes.map<TimelineItem>((n) => ({ kind: 'note', iso: n.createdAt, data: n })),
    ];
    items.sort((a, b) => new Date(a.iso).getTime() - new Date(b.iso).getTime());
    return items;
  }, [messages, notes]);

  // Group by day for separator rendering.
  const dayGroups = useMemo(() => {
    const groups: { day: string; items: TimelineItem[] }[] = [];
    for (const item of timeline) {
      const k = dayKey(item.iso);
      const last = groups[groups.length - 1];
      if (!last || last.day !== k) groups.push({ day: k, items: [item] });
      else last.items.push(item);
    }
    return groups;
  }, [timeline]);

  // Track whether the user has scrolled up — if so, don't auto-scroll
  // when new messages arrive.
  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    // 80px of slack so a tiny manual drag doesn't disable auto-scroll.
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setAutoScroll(atBottom);
  };

  // Auto-scroll on new tail message or on first paint.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const isFirstPaint = lastCountRef.current === 0 && messages.length > 0;
    const grew = messages.length > lastCountRef.current;
    if (isFirstPaint || (grew && autoScroll)) {
      el.scrollTop = el.scrollHeight;
    }
    lastCountRef.current = messages.length;
  }, [messages.length, autoScroll]);

  if (timeline.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-slate-400">
        <MessageSquare className="h-10 w-10" />
        <p className="mt-3 text-sm">No messages yet</p>
      </div>
    );
  }

  return (
    <div
      ref={scrollerRef}
      onScroll={onScroll}
      className="relative h-full overflow-y-auto bg-[#ECE5DD]/40 py-2"
      style={{
        // Subtle WhatsApp-ish wallpaper feel without an asset
        backgroundImage:
          'radial-gradient(rgba(0,0,0,0.025) 1px, transparent 1px)',
        backgroundSize: '14px 14px',
      }}
    >
      {dayGroups.map((g) => (
        <div key={g.day}>
          <DaySeparator iso={g.items[0].iso} />
          <div className="space-y-1.5">
            {g.items.map((it) =>
              it.kind === 'message' ? (
                <MessageBubble key={`m-${it.data.id}`} message={it.data} />
              ) : (
                <NoteBubble key={`n-${it.data.id}`} note={it.data} />
              ),
            )}
          </div>
        </div>
      ))}

      {/* "Jump to latest" pill when user has scrolled up */}
      {!autoScroll && (
        <button
          type="button"
          onClick={() => {
            const el = scrollerRef.current;
            if (el) el.scrollTop = el.scrollHeight;
            setAutoScroll(true);
          }}
          className="sticky bottom-3 mx-auto block rounded-full bg-indigo-500 px-3 py-1 text-[11px] font-medium text-white shadow-lg hover:bg-indigo-600"
        >
          ↓ Jump to latest
        </button>
      )}
    </div>
  );
}

function DaySeparator({ iso }: { iso: string }) {
  return (
    <div className="my-3 flex justify-center">
      <span className="rounded-md bg-white/80 px-2.5 py-0.5 text-[10.5px] font-medium text-slate-500 shadow-sm">
        {formatDaySeparator(iso)}
      </span>
    </div>
  );
}

function NoteBubble({ note }: { note: InternalNote }) {
  return (
    <div className="px-2">
      <div className="mx-auto max-w-[78%] rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-900 shadow-sm">
        <div className="mb-0.5 flex items-center justify-between gap-2 text-[10.5px] font-medium uppercase text-amber-700">
          <span>📝 Internal note · {note.author?.name ?? note.authorName}</span>
          <span>{formatMessageTime(note.createdAt)}</span>
        </div>
        <div className="whitespace-pre-wrap">{note.body}</div>
      </div>
    </div>
  );
}
